import React from 'react';
import { useDispatch } from 'react-redux';
import { Button, Grid, Typography } from '@mui/material';
import { Form } from 'formik';
import * as Yup from 'yup';
import { MESSAGE_CUSTOMER_CREDIT_ERROR, MESSAGE_CUSTOMER_CREDIT_SUCCESS } from 'config/messages';
import { MuiForm } from 'lib/Form';
import { PrefixInputFormField } from 'lib/form/fields/PrefixInputFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { Row } from 'ui/Flex';
import { enqueueSnackbar } from 'store/notifications/notificationsActions';
import { customerApi } from '../customerApi';
import { Customer } from '../models/Customer';

interface CustomerCreditFormProps {
    customer: Customer;
    onClose: () => void;
    currencySymbol: string;
    onCreditSuccess: (value: number) => void;
}

const initialValues = {
    balance: '',
    message: ''
};

const validationSchema = Yup.object().shape({
    balance: Yup.string().required('This field is required.'),
    message: Yup.string().required('This field is required.')
});

export const CustomerCreditForm: React.FC<CustomerCreditFormProps> = props => {
    const { customer, onClose, onCreditSuccess, currencySymbol } = props;
    const dispatch = useDispatch();
    const [isLoading, setLoading] = React.useState(false);

    const handleSubmit = React.useCallback(
        async ({ balance, message }: { balance: string; message: string }) => {
            setLoading(true);
            const result = await customerApi.credit(customer._id, {
                body: { number: Number(balance), description: message, scheme: 'BALANCE' }
            });
            setLoading(false);
            if (!result.ok) {
                return dispatch(enqueueSnackbar(MESSAGE_CUSTOMER_CREDIT_ERROR, { variant: 'error' }));
            }
            onCreditSuccess(Number(balance));
            onClose();
            return dispatch(enqueueSnackbar(MESSAGE_CUSTOMER_CREDIT_SUCCESS, { variant: 'success' }));
        },
        [customer._id, dispatch, onClose, onCreditSuccess]
    );

    return (
        <MuiForm onSubmit={handleSubmit} initialValues={initialValues} validationSchema={validationSchema}>
            {({ submitForm, isValid, isSubmitting }) => (
                <Form>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="h6">
                                {`Credit ${customer.firstName} with some balance`}
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2">
                                Gift your user some money for them to spend when they&apos;re making
                                purchases.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <PrefixInputFormField
                                name="balance"
                                label="Balance"
                                prefix={currencySymbol}
                                description="How much Balance would you like to award?"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextFormField
                                name="message"
                                label="Message"
                                multiline
                                rows={4}
                                description="Let the user know of this change."
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Row gutter align="flex-end">
                                <Button onClick={onClose} variant="outlined">
                                    Cancel
                                </Button>
                                <LoadingButton
                                    onClick={submitForm}
                                    disabled={!isValid || isSubmitting}
                                    loading={isLoading || isSubmitting}
                                    variant="contained"
                                >
                                    Credit
                                </LoadingButton>
                            </Row>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </MuiForm>
    );
};
